/**
 * InvitationCard Component
 *
 * Displays a pending reflection sharing invitation with Accept/Decline buttons.
 */

import { useState } from "react";
import { Loader2 } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import type { InvitationResponse } from "@/types/invitation";

interface InvitationCardProps {
  invitation: InvitationResponse;
  onAccept: (invitationId: string) => Promise<void>;
  onDecline: (invitationId: string) => Promise<void>;
}

export function InvitationCard({
  invitation,
  onAccept,
  onDecline,
}: InvitationCardProps) {
  const [accepting, setAccepting] = useState(false);
  const [declining, setDeclining] = useState(false);

  const handleAccept = async () => {
    setAccepting(true);
    try {
      await onAccept(invitation.id);
    } finally {
      setAccepting(false);
    }
  };

  const handleDecline = async () => {
    setDeclining(true);
    try {
      await onDecline(invitation.id);
    } finally {
      setDeclining(false);
    }
  };

  const timeAgo = formatDistanceToNow(new Date(invitation.created_at), {
    addSuffix: true,
  });

  return (
    <Card className="p-5">
      <div className="space-y-3">
        <div>
          <h3 className="font-semibold text-foreground text-base">
            &ldquo;{invitation.reflection_title || "Untitled Reflection"}&rdquo;
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            Shared by{" "}
            <span className="font-medium text-foreground">
              {invitation.sender_display_name || "another user"}
            </span>{" "}
            &middot; {timeAgo}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            onClick={handleAccept}
            disabled={accepting || declining}
            size="sm"
          >
            {accepting && <Loader2 className="w-3 h-3 animate-spin mr-1" />}
            Accept
          </Button>
          <Button
            variant="outline"
            onClick={handleDecline}
            disabled={accepting || declining}
            size="sm"
          >
            {declining && <Loader2 className="w-3 h-3 animate-spin mr-1" />}
            Decline
          </Button>
        </div>
      </div>
    </Card>
  );
}
